import { Menu } from "@headlessui/react";
import React, { useState, useEffect } from "react";
import axios from "axios";
import { BaseUrl } from "../BaseUrl";
import { 
  FaUserPlus, 
  FaUserCheck, 
  FaCalendarPlus, 
  FaMoneyBillWave,
  FaBell 
} from "react-icons/fa";
import { BiLoaderAlt } from "react-icons/bi";

// Helper function to get time ago
const getTimeAgo = (dateString) => {
  const now = new Date();
  const date = new Date(dateString);
  const seconds = Math.floor((now - date) / 1000);

  if (seconds < 60) return "Just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day${days > 1 ? "s" : ""} ago`;
  return date.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
};

// Get icon based on notification type
const getNotificationIcon = (type) => {
  switch (type) {
    case "invitation_sent":
      return <FaUserPlus />;
    case "invitation_accepted":
      return <FaUserCheck />;
    case "appointment_created":
      return <FaCalendarPlus />;
    case "payment_received":
      return <FaMoneyBillWave />;
    default:
      return <FaBell />;
  }
};

// Get icon background color based on type
const getIconBgClass = (type, isRead) => {
  if (isRead) return "bg-gray-100 text-gray-500";
  
  switch (type) {
    case "invitation_sent":
      return "bg-blue-100 text-blue-600";
    case "invitation_accepted":
      return "bg-green-100 text-green-600";
    case "appointment_created":
      return "bg-purple-100 text-purple-600";
    case "payment_received":
      return "bg-emerald-100 text-emerald-600";
    default:
      return "bg-subMain/10 text-subMain";
  }
};

function NotificationComp({ children }) {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch notifications
  const fetchNotifications = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem("token");
      
      if (!token) {
        setNotifications([]);
        return;
      }

      const response = await axios.get(`${BaseUrl}/doctor/notifications`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // console.log("Notifications API response:", response.data); // Debug log

      if (response.data.success) {
        setNotifications(response.data.data || []);
        setUnreadCount(response.data.pagination?.unreadCount || 0);
      } else {
        // If API returns success: false or unexpected response
        setNotifications([]);
        setUnreadCount(0);
      }
    } catch (err) {
      console.error("Error fetching notifications:", err);
      setError("Failed to load notifications");
      setUnreadCount(0); // Reset count on error
    } finally {
      setLoading(false);
    }
  };

  // Mark single notification as read
  const markAsRead = async (id) => {
    try {
      const token = localStorage.getItem("token");
      await axios.patch(
        `${BaseUrl}/doctor/notifications/${id}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setNotifications((prev) =>
        prev.map((n) => (n._id === id ? { ...n, isRead: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error("Error marking notification as read:", err);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.patch(
        `${BaseUrl}/doctor/notifications/read-all`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error("Error marking all as read:", err);
    }
  };

  // Fetch on menu open
  useEffect(() => {
    fetchNotifications();
  }, []);

  return (
    <Menu>
      <Menu.Button className="relative">
        {children}
        {unreadCount > 0 && (
          <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs font-bold text-white">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </Menu.Button>
      <Menu.Items className="absolute right-0 top-20 z-50 flex w-full flex-col gap-4 rounded-md bg-white px-6 py-4 shadow-lg ring-1 ring-border focus:outline-none sm:w-8/12 md:w-6/12 xl:w-2/6">
        <div className="flex-btn flex-wrap gap-4">
          <h2 className="text-md font-medium text-main">
            Notifications
            {unreadCount > 0 && (
              <span className="ml-2 text-sm font-normal text-gray-500">
                ({unreadCount} new)
              </span>
            )}
          </h2>
          <button
            onClick={markAllAsRead}
            disabled={unreadCount === 0}
            className="rounded-md px-4 py-2 text-sm text-subMain hover:bg-text disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Mark all read
          </button>
        </div>

        {/* Notifications List */}
        <div className="flex max-h-[500px] flex-col gap-4 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <BiLoaderAlt className="h-6 w-6 animate-spin text-subMain" />
            </div>
          ) : error ? (
            <div className="py-8 text-center text-sm text-red-500">{error}</div>
          ) : notifications.length === 0 ? (
            <div className="py-8 text-center text-sm text-gray-500">
              No notifications yet
            </div>
          ) : (
            notifications.map((item) => (
              <div
                key={item._id}
                onClick={() => !item.isRead && markAsRead(item._id)}
                className={`w-full cursor-pointer rounded-lg border p-4 transition-colors ${
                  item.isRead
                    ? "border-gray-200 bg-gray-50"
                    : "border-subMain/30 bg-white hover:bg-gray-50"
                }`}
              >
                <div className="grid items-center gap-4 xs:grid-cols-12">
                  <div className="xs:col-span-2">
                    <div
                      className={`text-md flex-colo h-12 w-12 rounded-full ${getIconBgClass(
                        item.type,
                        item.isRead
                      )}`}
                    >
                      {getNotificationIcon(item.type)}
                    </div>
                  </div>
                  <div className="xs:col-span-10">
                    <p
                      className={`text-sm ${
                        item.isRead ? "text-gray-500" : "text-textGray"
                      }`}
                    >
                      <span
                        className={`font-medium ${
                          item.isRead ? "text-gray-600" : "text-main"
                        }`}
                      >
                        {item.title}
                      </span>
                      {" - "}
                      {item.message}
                    </p>
                    <p className="mt-2 text-xs font-light text-textGray">
                      {getTimeAgo(item.createdAt)}
                    </p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </Menu.Items>
    </Menu>
  );
}

export default NotificationComp;
